import type { RenglonDeAlistamiento } from '@/lib/supabase/tipos-bd'

/**
 * LA CUENTA DE CADA MATERIAL: CUÁNTO SE PIDIÓ, CUÁNTO HAY Y CUÁNTO FALTA.
 *
 * Johan: «"No hay" significa que no hay x cantidad, pero se puede facturar la
 * cantidad que haya, y la cantidad que no haya pasa a una COT nueva».
 *
 * Lo que bodega escribe es texto («0,4», «5.6», vacío mientras borra): aquí
 * se vuelve número, sin pasarse de lo pedido ni bajar de cero. Es la misma
 * regla que aplica fn_separar_cotizacion, para que lo que se ve sea lo que
 * pasa.
 */

export type Linea = {
  id: number
  renglon: RenglonDeAlistamiento
  pedido: number
  hay: number
  falta: number
  /** Valor con IVA de lo que falta: lo que se va a la cotización nueva. */
  valorFalta: number
}

const redondo = (n: number) => Math.round(n * 100) / 100

/** Lo que propone el inventario de World Office: lo que hay, hasta lo pedido. */
export function sugerido(r: RenglonDeAlistamiento): number {
  return redondo(Math.min(Number(r.cantidad), Math.max(Number(r.existencia), 0)))
}

/** «0,4» → 0.4. Vacío o ilegible → null (se toma como lo sugerido). */
export function leer(texto: string | undefined): number | null {
  if (texto === undefined) return null
  let limpio = texto.trim().replace(/\s/g, '')
  // «1.200,5»: el punto es de miles. «0.4» o «0,4»: decimal.
  if (limpio.includes(',')) limpio = limpio.replace(/\./g, '').replace(',', '.')
  if (limpio === '') return null
  const n = Number(limpio)
  return Number.isFinite(n) ? n : null
}

/** Cantidades como las escribe la gente en Colombia: 0,4 · 5,6 · 1.200. */
export function cantidad(n: number): string {
  return n.toLocaleString('es-CO', { maximumFractionDigits: 2 })
}

/** Para la casilla: sin puntos de miles, que confunden al editar. */
export function paraEscribir(n: number): string {
  return n.toLocaleString('es-CO', { maximumFractionDigits: 2, useGrouping: false })
}

export function lineas(
  renglones: RenglonDeAlistamiento[],
  escrito: Record<number, string>,
): Linea[] {
  return renglones
    .filter((r) => r.id_inventario !== null)
    .map((r) => {
      const id = Number(r.id_inventario)
      const pedido = Number(r.cantidad)
      const hay = redondo(Math.min(Math.max(leer(escrito[id]) ?? sugerido(r), 0), pedido))
      const falta = redondo(pedido - hay)
      return {
        id,
        renglon: r,
        pedido,
        hay,
        falta,
        valorFalta: falta * Number(r.precio_unitario) * (1 + Number(r.iva ?? 0)),
      }
    })
}

/** Lo que se le manda a fn_separar_cotizacion: cuánto hay de cada material. */
export function cuantoHay(ls: Linea[]): Record<string, number> {
  return Object.fromEntries(ls.map((l) => [String(l.id), l.hay]))
}
